import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, FileText, Clock, Paperclip, Mail, Bot, User, Printer, AlertCircle } from 'lucide-react';
import api from '../services/api.js';
import { handleImprimirHistorial } from '../utils/printHistorial';

// Importación de componentes
import TimelineComunicaciones from '../components/HistorialSolicitudes/TimelineComunicaciones';
import DocumentosHistorial from '../components/HistorialSolicitudes/DocumentosHistorial';
import FormularioRespuesta from '../components/HistorialSolicitudes/FormularioRespuesta';
import GestionEstadoHistorial from '../components/HistorialSolicitudes/GestionEstadoHistorial';
import FacturacionCardInfo from '../components/FacturacionCardInfo.jsx';

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const ACTION_LABELS = {
  creacion:                     { label: 'Solicitud Ingresada',              color: '#2563eb', bg: '#eff6ff', icon: <FileText size={14} /> },
  cambio_estado:                { label: 'Cambio de Estado',                 color: '#7c3aed', bg: '#f5f3ff', icon: <Clock size={14} /> },
  asignacion:                   { label: 'Solicitud Asignada',               color: '#0284c7', bg: '#f0f9ff', icon: <User size={14} /> },
  cambio_estado_y_asignacion:   { label: 'Cambio de Estado y Asignación',    color: '#6366f1', bg: '#eef2ff', icon: <Clock size={14} /> },
  envio_cotizacion:             { label: 'Cotización Emitida',               color: '#d97706', bg: '#fffbeb', icon: <FileText size={14} /> },
  confirmacion_pago:            { label: 'Pago Confirmado',                  color: '#059669', bg: '#ecfdf5', icon: <FileText size={14} /> },
  rechazo_pago:                 { label: 'Pago Rechazado',                   color: '#dc2626', bg: '#fef2f2', icon: <AlertCircle size={14} /> },
  devolucion:                   { label: 'Devolución de Fondos',             color: '#d97706', bg: '#fffbeb', icon: <FileText size={14} /> },
  adjunto_documento:            { label: 'Documento Adjuntado',              color: '#059669', bg: '#ecfdf5', icon: <Paperclip size={14} /> },
  correo_enviado:               { label: 'Correo Enviado al Ciudadano',      color: '#d97706', bg: '#fffbeb', icon: <Mail size={14} /> },
  respuesta_ciudadano:          { label: 'Respuesta del Ciudadano',          color: '#0891b2', bg: '#ecfeff', icon: <User size={14} /> },
  rechazo:                      { label: 'Solicitud Rechazada',              color: '#dc2626', bg: '#fef2f2', icon: <AlertCircle size={14} /> },
  actualizacion:                { label: 'Actualización de Datos',           color: '#64748b', bg: '#f1f5f9', icon: <Bot size={14} /> },
};

function getActionMeta(action) {
  return ACTION_LABELS[action] || ACTION_LABELS.actualizacion;
}

/* ─── Modal Envío de Correo ────────────────────────────────────────────── */
import { Send } from 'lucide-react';
function ModalCorreo({ solicitud, onClose, onSent, prefill }) {
  const [asunto, setAsunto]   = useState(prefill?.asunto || '');
  const [mensaje, setMensaje] = useState(prefill?.mensaje || '');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!asunto.trim() || !mensaje.trim()) {
      toast.error('El asunto y el mensaje son obligatorios.');
      return;
    }
    setSending(true);
    try {
      await api.post(`/solicitudes/${solicitud.id}/correo`, { asunto, mensaje });
      toast.success('Correo enviado y registrado en el historial.');
      onSent();
      onClose();
    } catch {
      toast.error('Error al enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Send size={18} color="var(--primary)" /> Enviar Correo al Ciudadano
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Para: <strong>{solicitud.nombre_solicitante}</strong> ({solicitud.email})
          &nbsp;·&nbsp;Solicitud <strong>{solicitud.numero_seguimiento}</strong>
        </p>
        <div className="form-group">
          <label>Asunto</label>
          <input value={asunto} onChange={e => setAsunto(e.target.value)}
            placeholder="Ej: Información adicional requerida" />
        </div>
        <div className="form-group">
          <label>Mensaje</label>
          <textarea rows={5} value={mensaje} onChange={e => setMensaje(e.target.value)}
            placeholder="Escribe aquí el mensaje para el ciudadano..." />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <><Send size={14} /> Enviar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────── */
export default function HistorialSolicitud() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/gestion-solicitudes';

  const [solicitud, setSolicitud] = useState(null);
  const [estados, setEstados]     = useState([]);
  const [loading, setLoading]     = useState(true);

  // Documentos
  const [docOriginalUrl, setDocOriginalUrl]   = useState(null);
  const [loadingDocOrig, setLoadingDocOrig]   = useState(false);
  const [subirFiles, setSubirFiles]           = useState([]);
  const [subiendo, setSubiendo]               = useState(false);
  const [notificarCiudadano, setNotificarCiudadano] = useState(false);
  const [mensajeUpload, setMensajeUpload]     = useState('');

  // Correo modal
  const [modalCorreo, setModalCorreo] = useState(false);
  const [modalPrefill, setModalPrefill] = useState(null);

  useEffect(() => { fetchTodo(); }, [id]);

  const fetchTodo = async () => {
    setLoading(true);
    try {
      const [resSol, resEst] = await Promise.all([
        api.get(`/solicitudes/${id}`),
        api.get('/estados')
      ]);
      const sol = resSol.data.solicitud ?? resSol.data;
      sol.comprobante_url = resSol.data.comprobante_url;
      setSolicitud(sol);
      setEstados(resEst.data.filter(e => e.tipo === 'solicitud'));

      // Cargar URL firmada del doc original si tiene (o array de ellos)
      if (sol.s3_path || (sol.documentos_adjuntos && sol.documentos_adjuntos.length > 0)) {
        cargarDocOriginal(sol.id);
      }
    } catch {
      toast.error('Error al cargar la información de la solicitud.');
      navigate(from);
    } finally {
      setLoading(false);
    }
  };

  const cargarDocOriginal = async (solId) => {
    setLoadingDocOrig(true);
    try {
      const { data } = await api.get(`/solicitudes/${solId}/documento-original`);
      setDocOriginalUrl(data);
    } catch {
      setDocOriginalUrl(null);
    } finally {
      setLoadingDocOrig(false);
    }
  };

  const handleCambiarEstado = async (estadoId) => {
    try {
      await api.patch(`/solicitudes/${solicitud.id}/estado`, { estado_id: estadoId });
      toast.success('Estado actualizado correctamente');
      fetchTodo();
    } catch {
      toast.error('Error al cambiar el estado');
    }
  };

  const getEstadoNombre = (id) => {
    const st = estados.find(e => e.id === Number(id));
    return st ? st.nombre : `ID ${id}`;
  };

  const handleSubirDocs = async () => {
    if (!subirFiles.length) { toast.error('Selecciona al menos un archivo.'); return; }
    const fd = new FormData();
    subirFiles.forEach(f => fd.append('documentos[]', f));
    fd.append('notificar', notificarCiudadano ? '1' : '0');
    if (mensajeUpload.trim()) {
      fd.append('mensaje', mensajeUpload.trim());
    }
    setSubiendo(true);
    try {
      const { data } = await api.post(`/solicitudes/${solicitud.id}/documentos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const mensaje = data.notificado
        ? `${subirFiles.length} doc(s) adjuntados y ciudadano notificado por correo.`
        : `${subirFiles.length} doc(s) adjuntados correctamente.`;
      toast.success(mensaje);
      setSubirFiles([]);
      setNotificarCiudadano(false);
      setMensajeUpload('');
      fetchTodo();
    } catch {
      toast.error('Error al subir documentos.');
    } finally {
      setSubiendo(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '5rem' }}>
        <span className="spinner" style={{ width: '40px', height: '40px' }} />
        <p>Cargando historial de la solicitud...</p>
      </div>
    );
  }

  if (!solicitud) return null;

  const isRechazado = solicitud.estado?.nombre === 'Rechazado';

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,5vw,48px)', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Volver */}
      <button className="btn btn-secondary btn-sm"
        onClick={() => navigate(from)}
        style={{ marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
        <ChevronLeft size={16} /> Volver a Gestión de Solicitudes
      </button>

      {/* Header */}
      <div className="glass-card" style={{ marginBottom: '2rem', padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.8rem', color: 'var(--primary-dark)' }}>
              Solicitud <span style={{ fontFamily: 'monospace', color: 'var(--copper)' }}>{solicitud.numero_seguimiento || 'Sin número'}</span>
            </h1>
            <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-muted)' }}>
              RUT: <strong>{solicitud.rut_solicitante}</strong> — {solicitud.nombre_solicitante}
              {solicitud.rut_boleta && <>&nbsp;·&nbsp;RUT Boleta: <strong>{solicitud.rut_boleta}</strong></>}
              &nbsp;·&nbsp;
              {solicitud.email}
              {solicitud.telefono && <>&nbsp;·&nbsp;{solicitud.telefono}</>}
            </p>
            {solicitud.forma_entrega && (
              <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Entrega: <strong>{solicitud.forma_entrega}</strong>
                {solicitud.observaciones && <> · {solicitud.observaciones}</>}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <button 
              className="btn btn-sm no-print" 
              onClick={() => handleImprimirHistorial(solicitud)}
              style={{ background: '#475569', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Printer size={16} /> Imprimir Historial
            </button>
            <span className={`badge`} style={{
              fontSize: '1rem', padding: '0.5rem 1rem',
              backgroundColor: isRechazado ? '#fef2f2' : 'var(--bg-surface)',
              color: isRechazado ? '#ef4444' : 'var(--text)',
              border: isRechazado ? '1px solid #fca5a5' : '1px solid var(--border-color)',
              borderRadius: 8,
            }}>
              {solicitud.estado?.nombre}
            </span>
          </div>
        </div>
      </div>

      {/* Ficha de Facturación / Boleta */}
      <FacturacionCardInfo data={solicitud} style={{ marginBottom: '2rem' }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '2rem' }}>
        {/* ── COLUMNA IZQUIERDA: Timeline de Historial ────────────────── */}
        <TimelineComunicaciones 
          solicitud={solicitud}
          getActionMeta={getActionMeta}
          getEstadoNombre={getEstadoNombre}
          setModalPrefill={setModalPrefill}
          setModalCorreo={setModalCorreo}
        />

        {/* ── COLUMNA DERECHA: Acciones y Documentos ──────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          
          <DocumentosHistorial 
            solicitud={solicitud}
            loadingDocOrig={loadingDocOrig}
            docOriginalUrl={docOriginalUrl}
          />

          <FormularioRespuesta 
            solicitud={solicitud}
            subirFiles={subirFiles}
            setSubirFiles={setSubirFiles}
            notificarCiudadano={notificarCiudadano}
            setNotificarCiudadano={setNotificarCiudadano}
            mensajeUpload={mensajeUpload}
            setMensajeUpload={setMensajeUpload}
            handleSubirDocs={handleSubirDocs}
            subiendo={subiendo}
          />

          <GestionEstadoHistorial 
            solicitud={solicitud}
            estados={estados}
            handleCambiarEstado={handleCambiarEstado}
            setModalCorreo={setModalCorreo}
            isRechazado={isRechazado}
          />

        </div>
      </div>

      {/* Modal Correo */}
      {modalCorreo && solicitud && (
        <ModalCorreo
          solicitud={solicitud}
          prefill={modalPrefill}
          onClose={() => { setModalCorreo(false); setModalPrefill(null); }}
          onSent={fetchTodo}
        />
      )}
    </div>
  );
}
